import { Component, output, ChangeDetectionStrategy } from "@angular/core";
import { SelectionModel } from "@angular/cdk/collections";
import { take } from "rxjs";
import { SaleType } from "../../../models/saleType";
import { PropertyService } from "../../../services/PropertyService";

@Component({
  standalone: false,
  selector: 'app-sale-type-filter',
  templateUrl: './sale-type-filter.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./sale-type-filter.component.scss']
})
export class SaleTypeFilterComponent {
  readonly onSaleTypeSelectionChange = output<SaleType[] | undefined>();

  public saleTypes: SaleType[] = [];

  /** Checkbox state while the menu is open (not yet applied). */
  public saleTypeSelection = new SelectionModel<number>(true, []);

  /** Last applied selection — what menuLabel/allSaleTypesSelected reflect, and what we revert to on outside-click close. */
  private committedSaleTypeIds: number[] = [];

  constructor(private propertyService: PropertyService) {
    this.propertyService.getSaleTypes().pipe(take(1)).subscribe(saleTypes => {
      this.saleTypes = saleTypes;
    });
  }

  public presetSelectedSaleTypes(stArray: SaleType[] | undefined) {
    const ids = (stArray ?? []).map(st => st.id);
    this.committedSaleTypeIds = [...ids];
    this.saleTypeSelection = new SelectionModel<number>(true, ids);
  }

  get allSaleTypesSelected(): boolean {
    return this.committedSaleTypeIds.length === 0;
  }

  get menuLabel(): string {
    if (this.allSaleTypesSelected) return "All Sale Types";
    const selected = this.saleTypes.filter(st => this.committedSaleTypeIds.includes(st.id));
    if (!selected.length) return "All Sale Types";
    const [first, ...rest] = selected;
    return first.name + (rest.length > 0 ? ` (+${rest.length})` : "");
  }

  public isSaleTypeChecked(st: SaleType): boolean {
    return this.saleTypeSelection.isSelected(st.id);
  }

  public toggleAllSaleTypes(checked: boolean) {
    if (checked) this.saleTypeSelection.clear();
  }

  public toggleSaleType(st: SaleType) {
    this.saleTypeSelection.toggle(st.id);
  }

  /** Fires with the reason the menu closed: falsy = dismissed via outside click -> discard staged changes. */
  public onMenuClosed(reason: any) {
    if (!reason) {
      this.saleTypeSelection = new SelectionModel<number>(true, this.committedSaleTypeIds);
    }
  }

  public onApplyClick() {
    this.committedSaleTypeIds = [...this.saleTypeSelection.selected];
    this.emitSelection();
  }

  public onClearClick() {
    this.saleTypeSelection.clear();
    this.committedSaleTypeIds = [];
    this.emitSelection();
  }

  private emitSelection() {
    if (this.allSaleTypesSelected) {
      this.onSaleTypeSelectionChange.emit(undefined);
      return;
    }

    const stArray = this.saleTypes.filter(st => this.saleTypeSelection.isSelected(st.id));
    this.onSaleTypeSelectionChange.emit(stArray);
  }
}